import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { memo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CookiesIcon from "@mui/icons-material/Cookie";
import { getCookieConsent, saveCookieConsent } from "Utils/cookieConsent";

const CookieBanner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [showBanner, setShowBanner] = useState(() => !getCookieConsent());
  const [showSettings, setShowSettings] = useState(false);
  const [consentSettings, setConsentSettings] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  const handleAcceptAll = () => {
    saveCookieConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    });
    setShowBanner(false);
  };

  const handleRejectOptional = () => {
    saveCookieConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    });
    setShowBanner(false);
  };

  const handleSaveSettings = () => {
    saveCookieConsent({
      ...consentSettings,
      timestamp: new Date().toISOString(),
    });
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleOpenSettings = () => {
    setShowSettings(true);
  };

  const handleCloseSettings = () => {
    setShowSettings(false);
  };

  const handleSettingsChange = (event) => {
    setConsentSettings({
      ...consentSettings,
      [event.target.name]: event.target.checked,
    });
  };

  const handlePolicyClick = (path) => () => {
    navigate(path);
    handleCloseSettings();
  };

  if (!showBanner) return null;

  const bannerContent = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-around",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "flex-start" : "center",
        gap: isMobile ? 2 : 3,
        p: isMobile ? 2.5 : 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minWidth: isMobile ? "auto" : "fit-content",
        }}
      >
        <CookiesIcon sx={{ color: "#01AAF5", fontSize: 28 }} />
        <Typography
          variant={isMobile ? "body2" : "body1"}
          sx={{
            color: "#333",
            fontWeight: 500,
            lineHeight: 1.4,
          }}
        >
          We use cookies to improve your experience. By continuing to use our
          site, you agree to our{" "}
          <Typography
            component="span"
            onClick={handlePolicyClick("/cookie-policy")}
            sx={{
              color: "#01AAF5",
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: 600,
              "&:hover": { color: "#0190c7" },
            }}
          >
            Cookie Policy
          </Typography>
          .
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 1.5 : 2,
          flexShrink: 0,
          width: isMobile ? "100%" : "auto",
          "& button": {
            whiteSpace: "nowrap",
          },
        }}
      >
        <Button
          variant="outlined"
          onClick={handleOpenSettings}
          sx={{
            borderColor: "#01AAF5",
            color: "#01AAF5",
            fontWeight: 600,
            "&:hover": {
              borderColor: "#0190c7",
              backgroundColor: "rgba(1, 170, 245, 0.08)",
            },
          }}
        >
          Settings
        </Button>
        <Button
          variant="outlined"
          onClick={handleRejectOptional}
          sx={{
            borderColor: "#666",
            color: "#666",
            fontWeight: 600,
            "&:hover": {
              borderColor: "#444",
              backgroundColor: "rgba(102, 102, 102, 0.08)",
            },
          }}
        >
          Reject
        </Button>
        <Button
          variant="contained"
          onClick={handleAcceptAll}
          sx={{
            backgroundColor: "#01AAF5",
            color: "white",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#0190c7",
            },
          }}
        >
          Accept All
        </Button>
      </Box>
    </Box>
  );

  const BannerWrapper = isMobile ? Box : Slide;

  const bannerComponent = (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: "#fff",
        boxShadow: isMobile
          ? "0 -4px 20px rgba(0,0,0,0.1)"
          : "0 -4px 24px rgba(0,0,0,0.12)",
        borderTop: isMobile ? "none" : "1px solid rgba(0,0,0,0.08)",
      }}
    >
      {isMobile ? (
        bannerContent
      ) : (
        <BannerWrapper
          direction="up"
          in={showBanner}
          mountOnEnter
          unmountOnExit
        >
          {bannerContent}
        </BannerWrapper>
      )}
    </Box>
  );

  return (
    <>
      {bannerComponent}

      <Dialog
        open={showSettings}
        onClose={handleCloseSettings}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Slide}
        transitionDuration={300}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? "16px 16px 0 0" : "16px",
            p: isMobile ? 2 : 3,
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{
                fontWeight: 700,
                color: "#333",
                mb: 1,
              }}
            >
              Cookie Settings
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Manage your cookie preferences. Necessary cookies are essential
              for the website to function properly.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <FormControlLabel
              control={
                <Checkbox checked={consentSettings.necessary} disabled />
              }
              label={
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "#333" }}
                  >
                    Necessary Cookies
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    Required for the website to function. Cannot be disabled.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: "flex-start", m: 0 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={consentSettings.analytics}
                  onChange={handleSettingsChange}
                  name="analytics"
                  sx={{ color: "#01AAF5" }}
                />
              }
              label={
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "#333" }}
                  >
                    Analytics Cookies
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    Help us understand how visitors interact with our website.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: "flex-start", m: 0 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={consentSettings.marketing}
                  onChange={handleSettingsChange}
                  name="marketing"
                  sx={{ color: "#01AAF5" }}
                />
              }
              label={
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "#333" }}
                  >
                    Marketing Cookies
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    Used to provide relevant ads and marketing campaigns.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: "flex-start", m: 0 }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 4,
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCloseSettings}
              fullWidth={isMobile}
              sx={{
                borderColor: "#666",
                color: "#666",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#444",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveSettings}
              fullWidth={isMobile}
              sx={{
                backgroundColor: "#01AAF5",
                color: "white",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "#0190c7",
                },
              }}
            >
              Save Preferences
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default memo(CookieBanner);
